import { AsyncPipe, DecimalPipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  input
} from '@angular/core';
import { TNumericFormatPattern, TNumericFormatType, ToDecimalPipe } from '@partners/utils';
import { BehaviorSubject, Observable } from 'rxjs';

export interface INumericFormatParts {
  prefix: string;
  value: string;
  fractions: string;
  suffix: string;
}

@Component({
  standalone: true,
  selector: 'numeric-label',
  template: `
    <ng-container *ngIf="format$ | async as format">
      <span class="prefix" *ngIf="format?.prefix">{{ format.prefix }}</span>
      <span class="value">{{ format.value }}</span>
      <span class="fractions" *ngIf="format?.fractions">.{{ format.fractions }}</span>
      <span class="suffix" *ngIf="format?.suffix">{{ format.suffix }}</span>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, AsyncPipe, ToDecimalPipe],
  providers: [DecimalPipe, ToDecimalPipe],
  host: {
    '[class.unified-size]': 'isUnifiedSize()'
  }
})
export class NumericLabelComponent implements OnChanges {
  @Input()
  value: string | number;

  @Input()
  presetFormat: TNumericFormatType = 'decimal';

  @Input()
  formatPattern: TNumericFormatPattern = '1.0-0';

  @Input()
  isAbsoluteMode: boolean;

  isUnifiedSize = input(true);

  get format$(): Observable<INumericFormatParts> {
    return this._readyEmitter.asObservable();
  }

  get currencySymbol(): string {
    return '₪';
  }

  get percentSymbol(): string {
    return '%';
  }

  get fractionDigits(): { min: number; max: number } {
    const fractionsIndex = this.formatPattern.indexOf('.');
    const fractionsPattern = this.formatPattern.slice(fractionsIndex + 1);
    const fractionsParts = fractionsPattern.split('-');
    return {
      min: parseInt(fractionsParts[0]) || 0,
      max: parseInt(fractionsParts[1]) || 0
    };
  }

  @HostBinding('class.numeric-label')
  hostClass: boolean = true;

  formatParts$: Observable<INumericFormatParts>;

  private _readyEmitter: BehaviorSubject<INumericFormatParts>;

  constructor(private decimalPipe: ToDecimalPipe, private changeDetector: ChangeDetectorRef) {
    this._readyEmitter = new BehaviorSubject<INumericFormatParts>({
      prefix: '',
      suffix: '',
      value: '',
      fractions: ''
    });
  }

  /**
   * @description
   * on any given change of value, we reformat it, this is done here, to accommodate
   * both sync and async values originated in the consumer
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    const { value } = changes;
    const { currentValue, previousValue } = value;

    if (currentValue !== previousValue) {
      this.formatNumericValue();
      this.changeDetector.detectChanges();
    }
  }

  /**
   * @description
   * converts raw numeric value into an object with format parts (prefix | suffix | value | fractions)
   * after format is done, the formatted string is broken into its parts to allow wrapping each part
   * in its own html element for different styling
   * examples:
   * 1). value=12587.62 => ₪12,587.62 => prefix:₪ | suffix:'' | value:12,587 | fractions:62
   * 2). value=6.8745 => 6.8745%  => prefix:'' | suffix:'%' | value:6 | fractions:8745
   *
   * @returns
   */
  private formatNumericValue(): void {
    let format: INumericFormatParts = {
      prefix: '',
      suffix: '',
      value: '',
      fractions: ''
    };

    const formattedValue = this.decimalPipe.transform(this.value, this.formatPattern);

    if (!formattedValue) {
      this._readyEmitter.next(format);
      return;
    }

    /** parse the prefix & suffix */
    Object.assign(format, {
      prefix: this.getPrefixValue(),
      suffix: this.getSuffixValue(),
      fractions: this.getFractionsValue(formattedValue),
      value: this.getNumericValue(formattedValue)
    });

    this._readyEmitter.next(format);
  }

  /**
   * @description
   * cut the numeric portion of a fully-formatted numeric string. examples:
   * if formattedValue = "12,541.0896%" => then numeric portion is => "12,541"
   * if formattedValue = "₪55,102" => then numeric portion is => "55,102"
   * @param formattedValue the fully-formatted string value (example: "12,541.0896%")
   * @return the numeric value of a fully formatted string
   */
  private getNumericValue(formattedValue: string): string {
    formattedValue = formattedValue.trim();

    /** we want to display empty numeric format as single "0" value */
    if (formattedValue === '0') {
      return formattedValue;
    }

    /** in absolute mode - don't accept negative values */
    if (this.isAbsoluteMode && parseFloat(formattedValue) < 0) {
      const negativeIndex = formattedValue?.indexOf('-');
      formattedValue = formattedValue.slice(negativeIndex + 1);
    }

    /** slice off any fraction (decimal) digits */
    const fractionIndex = formattedValue?.indexOf('.');

    if (fractionIndex < 0) {
      return formattedValue;
    }

    return formattedValue.slice(0, fractionIndex);
  }

  /**
   * @description
   * cut the fractions digits (decimal digits) portion of a fully-formatted numeric string. examples:
   * if formattedValue = "12,541.0896%" => "0896"
   * if formattedValue = "₪55,102.55" => "55"
   * @param formattedValue the fully-formatted string value (example: "12,541.0896%")
   * @return the numeric value of a fully formatted string
   */
  private getFractionsValue(formattedValue: string): string {
    const { min, max } = this.fractionDigits;
    const fractionIndex = formattedValue?.indexOf('.');
    if (fractionIndex > 0 && (min || max)) {
      return formattedValue.slice(fractionIndex + 1);
    }
    return null;
  }

  private getPrefixValue(): string | null {
    switch (this.presetFormat) {
      case 'money':
        return this.currencySymbol;
      default:
        return null;
    }
  }

  private getSuffixValue(): string | null {
    switch (this.presetFormat) {
      case 'percent':
        return this.percentSymbol;
      default:
        return null;
    }
  }
}
